import React, { useState, useEffect, useRef } from "react";
import { SubtleButton, SubtleIconButton } from "components/button";
import { useMutation } from "react-query";
import Axios from "axios";
import { MdImage, MdPictureAsPdf, MdMoreVert } from "react-icons/md";
import Popper from "components/react-popper";
import { useTranslation } from 'react-i18next';
import { LuSave } from "react-icons/lu";
import { useSnackbarContext } from "components/snackbar";
import { useModalContext } from "components/modal";
import EditFileModal from "./EditFileModal";
import TooltippedElement from "components/TooltippedElement";

const INVALID_CHARACTERS_REGEX = /[\/:*?"<>|]/;

interface CustomFile extends File {
  new?: boolean; 
  url?: string; 
  thumbnail?: string;
  isPdf?: boolean;
}

type Props = {
  id: string; // request or project id
  type: string; // request or project
  files: CustomFile[];
  setFiles?: (files: CustomFile[]) => void; // used locally to update files
  refetch: () => void;
  isLocal?: boolean;
};

const FileGrid = (props: Props) => {
  const { t } = useTranslation();
  const tBase = "components.file-grid.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const { showSnackbar } = useSnackbarContext();
  const { setModal } = useModalContext();

  const [openPopperIndex, setOpenPopperIndex] = useState<number | null>(null);
  const [isEdit, setIsEdit] = useState<number | null>(null);
  const [editedName, setEditedName] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [gridCols, setGridCols] = useState(2);
  const gridRef = useRef<HTMLDivElement>(null); 

  const { files: filesObj, setFiles, id, type, refetch, isLocal } = props;

  useEffect(() => {
    if (!gridRef.current) return;
  
    let resizeTimer: number;
  
    const updateGridCols = () => {
      if (!gridRef.current) return;
      const width = gridRef.current.offsetWidth;
  
      let newCols = 2;
      if (width > 1200) newCols = 4;
      else if (width > 800) newCols = 3;
      else if (width > 500) newCols = 2;
  
      // Only update state if it has changed
      setGridCols((prev) => (prev !== newCols ? newCols : prev));
    };
  
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(resizeTimer);
      resizeTimer = requestAnimationFrame(updateGridCols);
    });
  
    observer.observe(gridRef.current);
    updateGridCols(); // Initial call
  
    return () => {
      observer.disconnect();
      cancelAnimationFrame(resizeTimer);
    };
  }, []);  

  const files = filesObj.map((file: any) => {
    // the alternatives to file.Key or file.signedUrl depend where the files are coming from
    const isPdf = file.Key ? file.Key.includes("pdf") : file.name.includes("pdf");
    return {
      ...file,
      name: file.Key ? file.Key : file.name,
      url: file.signedUrl ? file.signedUrl : file.url,
      thumbnail: file.new ? file.thumbnail : !isLocal ? file.signedUrl : file.url,
      isPdf: isPdf,
    };
  });

  const getFileS3 = useMutation(
    async (file: CustomFile) => {
      return await Axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/files/${type}/${id}/${encodeURIComponent(file.name)}`
      ).then((res) => res.data);
    },
    {
      onSuccess: (data) => {
        window.open(data, "_blank") || window.location.replace(data);
        refetch();
      }
    }
  );

  // used for getting newly uploaded files
  const getFileNew = async (file: CustomFile) => {
    if (file.url) {
      window.open(file.url, "_blank") || window.location.replace(file.url);
      return null;
    } else {
      throw new Error("No preview URL available for this file.");
    }
  };

  const deleteFileLocally = (file: File, i: number) => {
    const fileName = file.name;
    const newFiles = files.filter((file) => file.name !== fileName);
    setFiles && setFiles(newFiles);
    setOpenPopperIndex(null);
  };

  
  const editFile = useMutation(
    async ({ oldKey, newKey }: { oldKey: string; newKey: string }) => {
      if (oldKey === newKey) {
        setModal();
        return;
      };
  
      const res = await Axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/files/${type}/${id}/${encodeURIComponent(oldKey)}`,
        { newKey }
      );

  
      return res.data;
    },
    {
      onSuccess: () => {
        setModal();
        refetch();
        showSnackbar(tr("File updated!"));
      },
      onError: (error) => {
        console.error("Error updating file", error); 
        showSnackbar(tr("Error updating file"));
      },
    }
  );

  const deleteFileS3 = useMutation(
    async (file: CustomFile) => {
      return await Axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/files/${type}/${id}/${encodeURIComponent(file.name)}`
      ).then((res) => res.data);
    },
    {
      onSuccess: () => {
        refetch();
        showSnackbar(tr("File deleted!"));
      },
    }
  );

  const editModal = (file: CustomFile) => {
    setModal({
      label: tr("Edit File"),
      component: (
        <EditFileModal
          files={files}
          oldKey={file.name}
          newKey={file.name}
          editFile={editFile.mutate}
          setModal={setModal}
          ext={file.name.split(".").pop()}
          isLoading={editFile.isLoading}
        />
      ),
    });
  };

  const onSave = (file: CustomFile) => {
    if (!editedName) {
      setIsEdit(null);
      return; 
    }

    const ext = file.name.split(".").pop();
    const baseFileName = file.name.split(".")[0];
    if (editedName === baseFileName) {
      setIsEdit(null);
      return;
    }

    if (INVALID_CHARACTERS_REGEX.test(editedName)) {
      showSnackbar(tr("A file name can't contain any of the following characters") + `: \\ / : * ? \" < > |`);
      setIsError(true);
      setTimeout(() => setIsError(false), 500);
      return;
    }

    const exists = files.find((f) => f.name === `${editedName}.${ext}`);
    if (exists) {
      showSnackbar(tr("File with that name already exists"));
      setIsError(true);
      setTimeout(() => setIsError(false), 500);
      return;
    }

    const newFiles = files.map((f, i) => {
      if (i === isEdit) {
        return {
          ...f,
          editedName: editedName + "." + ext,
        };
      }
      return f;
    });

    setFiles && setFiles(newFiles);
    setEditedName(null);
    setIsEdit(null);
  };

  return (
    <div ref={gridRef} className={`grid grid-cols-${gridCols} gap-4`}>
      {files.map((file: any, i: number) => { 
        const sizeInMB = (file.size ?? file.fullFile?.size ?? 0) / (1024 * 1024);
        const sizeStr = `${(sizeInMB).toFixed(2)} MB`;
        
        return (
          <div key={i} className="border border-gray-200 bg-gray-100 p-1 rounded-lg w-full relative">
            {/* Display File Size in MB */}
            <div className="absolute top-1 right-1 text-xs text-gray-500 bg-slate-50 rounded-md px-[1px]">
              {sizeStr}
            </div>
            {/* Image */}
            <button
              onClick={() => {
                if (file.new) {
                  getFileNew(file);
                } else {
                  getFileS3.mutate(file);
                }
              }}
              className="w-full h-[150px]"
            >
              <img
                src={file.isPdf ? file.thumbnail : file.url}
                className="w-full h-full object-cover rounded-lg"
                alt={file.name}
              />
            </button>
            <div className="flex items-center rounded-lg h-10">
              {/* Icon */}
              <div className="w-fit mr-1">
                {file.name.includes("pdf") ? (
                  <MdPictureAsPdf className="text-red-500" size={20} />
                ) : (
                  <MdImage className="text-green-500" size={20} />
                )}
              </div>
              {/* Name || Input */}
              <div className="overflow-hidden">
                {isEdit === i && isLocal ? (
                  <div className="flex items-center mr-1">
                    <input
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          onSave(file);
                        }
                      }}
                      type="text"
                      className={`
                        w-full border border-gray-200 rounded-lg p-1
                        ${
                          isError
                            ? "animate-shake focus:outline-red-400"
                            : "focus:outline-blue-300"
                        }
                      `}
                      value={
                        editedName !== null && editedName !== undefined
                          ? editedName
                          : file.editedName
                          ? file.editedName.split(".")[0]
                          : file.name.split(".")[0]
                      }
                      onChange={(e) => {
                        setEditedName(e.target.value);
                      }}
                    />
                    <div className="">
                      .{file.name.split(".").pop()}
                    </div>
                  </div>
                ) : (
                  <>
                    <TooltippedElement
                      message={file.editedName || file.name}
                      placement="bottom"
                      element={
                        <div className="truncate w-full overflow-hidden">
                          {file.editedName || file.name}
                        </div>
                      }
                    />
                  </>
                )}
              </div>
              {/* Popper || Save */}
              <div className="w-[20px] flex justify-center ml-auto">
                {isEdit === i && isLocal ? (
                  <div className="">
                    <SubtleButton
                      onClick={() => {
                        onSave(file);
                      }}
                    >
                      <LuSave className="text-secondary" size={20} />
                    </SubtleButton>
                  </div>
                ) : (
                  <Popper
                    clickable
                    placement="bottom-end"
                    reference={
                      <SubtleIconButton
                        size="small"
                        onClick={() =>
                          setOpenPopperIndex(openPopperIndex === i ? null : i)
                        }
                      >
                        <MdMoreVert />
                      </SubtleIconButton>
                    }
                  >
                    {({ disable }: { disable: () => void }) => (
                      <div className="flex flex-col">
                        <SubtleButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsEdit(i);
                            !isLocal && editModal(file);
                            disable();
                          }}
                        >
                          {tr("Edit")}
                        </SubtleButton>
                        <SubtleButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isLocal) {
                              deleteFileLocally(file, i);
                            } else {
                              deleteFileS3.mutate(file);
                            }
                            disable();
                          }}
                        >
                          {tr("Delete")}
                        </SubtleButton>
                      </div>
                    )}
                  </Popper>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  );
};

export default FileGrid;